import { motion } from 'framer-motion';
import { LayoutGrid, ChefHat } from 'lucide-react';
import { KitchenStation } from '../../types/kds';

interface StationTabsProps {
  stations: KitchenStation[];
  selectedStationId: string | null;
  onSelectStation: (stationId: string | null) => void;
  orderCounts: Record<string, number>;
}

export function StationTabs({
  stations,
  selectedStationId,
  onSelectStation,
  orderCounts,
}: StationTabsProps) {
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-2 custom-scrollbar">
      {/* All stations tab */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => onSelectStation(null)}
        className={`relative flex items-center gap-2.5 px-4 py-2.5 rounded-xl font-medium text-sm
          transition-all duration-200 whitespace-nowrap
          ${
            selectedStationId === null
              ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/25'
              : 'bg-gray-100 dark:bg-white/[0.06] text-gray-700 dark:text-white/70 hover:bg-gray-200 dark:hover:bg-white/[0.1] border border-gray-200 dark:border-white/[0.08]'
          }`}
      >
        <LayoutGrid className="w-4 h-4" />
        <span>Tutti</span>
        {orderCounts.total > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className={`flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-xs font-bold
              ${selectedStationId === null ? 'bg-white/25 text-white' : 'bg-orange-100 dark:bg-orange-500/20 text-orange-600 dark:text-orange-400'}`}
          >
            {orderCounts.total}
          </motion.div>
        )}
      </motion.button>

      {/* Station tabs */}
      {stations.map((station) => {
        const count = orderCounts[station.id] || 0;
        const isSelected = selectedStationId === station.id;

        return (
          <motion.button
            key={station.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onSelectStation(station.id)}
            className={`relative flex items-center gap-2.5 px-4 py-2.5 rounded-xl font-medium text-sm
              transition-all duration-200 whitespace-nowrap
              ${
                isSelected
                  ? 'text-white shadow-lg'
                  : 'bg-gray-100 dark:bg-white/[0.06] text-gray-700 dark:text-white/70 hover:bg-gray-200 dark:hover:bg-white/[0.1] border border-gray-200 dark:border-white/[0.08]'
              }`}
            style={{
              backgroundColor: isSelected ? station.color : undefined,
              boxShadow: isSelected ? `0 8px 24px ${station.color}40` : undefined,
            }}
          >
            <ChefHat className="w-4 h-4" />
            <span>{station.name}</span>
            {count > 0 && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className={`flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-xs font-bold
                  ${isSelected ? 'bg-white/25 text-white' : 'bg-gray-200 dark:bg-white/[0.1] text-gray-700 dark:text-white/70'}`}
              >
                {count}
              </motion.div>
            )}
          </motion.button>
        );
      })}
    </div>
  );
}
